import { Close } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Divider, FormControl, IconButton, InputLabel, MenuItem, Modal, OutlinedInput, Select, TextField, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import React, { startTransition, Suspense, useEffect, useState } from "react";
import { Color } from "../../../../GlobalStyles";
import { lazy } from "react";
import CCService from "../../../../services/httpServiceWithoutSuperAdmin";
import { formatSalary } from "../../../../functions/salary-formatter";
import { useQueryClient } from "@tanstack/react-query";
import { useMentorRoles } from "../../../../hooks/useMentorRoles";
import { useNavigate } from "react-router-dom";
const AboutEditor = lazy(() => import("./AboutEditor").then(module => ({ default: module.AboutEditor })));
function JobCard({ job }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const jobType = job.jobType || "Not specified";
  const title = job.title || "Untitled Position";
  const company = job.company || "Untitled Company";
  const description = job.jobDescription || "No description available";
  const status = job.jobStatus || "In-Review";
  const service = new CCService();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState({
    accept: false,
    reject: false,
  });

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleAccept = async () => {
    try {
      setLoading({ accept: true });
      const response = await service.put(`${process.env.REACT_APP_RD_API_URL}/jobs/job`, { id: job.id, jobStatus: "Live" });

      if (response.status === 200) {
        queryClient.setQueriesData(["jobs"], (oldData) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.map((j) =>
                j.id === job.id ? { ...job, jobStatus: "Live" } : j
              )
            }))
          };
        });
      }
      setLoading({ accept: false });
      setShowModal(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      setLoading({ accept: false });
    }
  };

  const handleReject = async () => {
    try {
      setLoading({ reject: true });
      const response = await service.put(`${process.env.REACT_APP_RD_API_URL}/jobs/job`, { id: job.id, jobStatus: "Closed", closed_by: "Admin" });

      if (response.status === 200) {
        queryClient.setQueriesData(["jobs"], (oldData) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.map((j) =>
                j.id === job.id ? { ...job, jobStatus: "Closed" } : j
              )
            }))
          };
        });
      }
      setLoading({ reject: false });
      setShowModal(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      setLoading({ reject: false });
    }
  };

  const handleSave = async (editedJob) => {
    try {
      const payload = {
        id: editedJob.id,
        title: editedJob.title,
        jobDescription: editedJob.jobDescription || null,
        location: editedJob.location || "Remote",
        jobType: editedJob.jobType,
        salary: editedJob.salary,
        jobStatus: editedJob.jobStatus,
        jobDescriptionURL: editedJob.jobDescriptionURL || null,
        about: editedJob.about || null,
        mentor_role_id: editedJob.mentor_role_id || null
      }
      console.log("payload", payload, editedJob);
      const response = await service.put(`${process.env.REACT_APP_RD_API_URL}/jobs/job`, payload);

      if (response.status === 200) {
        queryClient.setQueriesData(["jobs"], (oldData) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.map((job) =>
                job.id === editedJob.id ? { ...editedJob, about: editedJob.about, mentor_role_id: editedJob.mentor_role_id } : job
              )
            }))
          };
        });
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  const handleJobNav = () => {
    navigate(`/AllRequests/Candidates?jobId=${job.id}&tab=all`);
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -3 }}
      transition={{ duration: 0.25 }}
      className="mx-3"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#fafafa",
          border: "1px solid #e5e5e5",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Box sx={{ flex: 3, minWidth: "200px" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#222" }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            {company} • {jobType}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              color: "#444",
              fontSize: "0.88rem",
              maxWidth: "90%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description || "No description available"}
          </Typography>
        </Box>

        <Typography
          sx={{
            flex: 1,
            textAlign: "center",
            textTransform: "capitalize",
            color: status === "Live" ? "green" : "gray",
            fontWeight: 500,
          }}
        >
          {status}
        </Typography>

        <Box
          sx={{
            flex: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            minWidth: "220px",
          }}
        >
          {status === "Live" && (
            <Button
              onClick={handleJobNav}
              sx={{
                backgroundColor: Color.primary3 || "#00B8D9",
                color: "#fff",
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: 2,
                "&:hover": { backgroundColor: "#0091aa" },
              }}
            >
              View Student
            </Button>
          )}
          <Button
            onClick={handleOpenModal}
            sx={{
              backgroundColor: "#00B8D9",
              color: "#fff",
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#0091aa" },
            }}
          >
            View
          </Button>
        </Box>

        <CustomModal
          showModal={showModal}
          handleCloseModal={handleCloseModal}
          job={job}
          handleAccept={handleAccept}
          handleReject={handleReject}
          handleSave={handleSave}
          loading={loading}
        />
      </Box>

      {/* <Divider /> */}
    </motion.div>
  );
}

export default JobCard;

const slideInVariants = {
  hidden: { opacity: 0, x: 300 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 300 },
};

const CustomModal = ({ showModal, handleCloseModal, job, handleAccept, handleReject, handleSave, loading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(job);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { data: mentorRoles } = useMentorRoles();
  const service = new CCService();

  useEffect(() => {
    setEditedJob(job);
  }, [job]);

  const toggleEdit = async () => {
    if (isEditing) {
      try {
        await handleSave(editedJob);
      } catch (error) {
        console.error("Error saving job:", error);
        return;
      }
      startTransition(() => setIsEditing(false));
    } else {
      startTransition(() => setIsEditing(true));
    }
  };

  const handleFieldChange = (field) => (e) => {
    if (field === "salary") {
      setEditedJob((prev) => ({
        ...prev,
        salary: { max: e.target.value, min: prev.salary.min },
      }));
      return;
    }
    setEditedJob((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Helper to render a field either as plain text or as a text field when editing
  const renderField = (label, fieldValue, fieldKey) => {
    return isEditing ? (
      <TextField fullWidth label={label} value={editedJob[fieldKey]} onChange={handleFieldChange(fieldKey)} variant="outlined" size="small" InputProps={{ style: { height: 35 } }} sx={{ mb: 1 }} />
    ) : (
      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>{label}:</strong> {fieldValue}
      </Typography>
    );
  };

  const handleAboutChange = (html) => {
    setEditedJob((prev) => ({ ...prev, about: html }));
  };

  const handleRegenerate = async () => {
    try {
      setIsRegenerating(true);
      const response = await service.put(`${process.env.REACT_APP_RD_API_URL}/jobs/job-about?id=${job.id}`);
      if (response.status === 200) {
        console.log("response", response.data.about);
        setEditedJob((prev) => ({ ...prev, about: response.data.about }));
      }
      setIsRegenerating(false);
    } catch (err) {
      console.error("Error regenerating job about:", err);
    } finally {
      setIsRegenerating(false);
    }
  };


  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 250,
      },
    },
  };

  // console.log("editedJob", editedJob);
  return (
    <Modal open={showModal} onClose={handleCloseModal}>
      <Box
        component={motion.div}
        initial={{ x: "100%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          backgroundColor: "#fff",
          height: "100vh",
          width: { xs: "100%", sm: "90%", md: "600px" },
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          overflowY: "auto",
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {isEditing ? <TextField fullWidth value={editedJob.title} onChange={handleFieldChange("title")} variant="outlined" label="Job Title" size="small" InputProps={{ style: { height: 35 } }} sx={{ mr: 2 }} /> : <Typography sx={{ fontWeight: "bold", fontSize: "24px", flex: 1 }}>{job.title}</Typography>}
          <IconButton
            onClick={() => {
              setEditedJob(job);
              setIsEditing(false);
              handleCloseModal();
            }}
            sx={{ color: "gray" }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, my: 2 }}>
          {(job.jobStatus === "In-Review" || job.jobStatus === "Closed") && (
            <Button disabled={loading.accept} variant="contained" color="primary" onClick={handleAccept}>
              {
                loading.accept ? <CircularProgress size={20} /> : job.jobStatus === "In-Review" ? "Accept" : "Reopen"
              }
            </Button>
          )}
          {(job.jobStatus === "In-Review" || job.jobStatus === "Live") && (
            <Button disabled={loading.reject} variant="outlined" color="error" onClick={handleReject}>
              {
                loading.reject ? <CircularProgress size={20} /> : job.jobStatus === "In-Review" ? "Reject" : "Close"
              }
            </Button>
          )}
          <Button variant="outlined" onClick={toggleEdit} sx={{ marginLeft: "auto" }}>
            {isEditing ? "Save" : "Edit"}
          </Button>
        </Box>

        {/* Job Details */}
        <Box sx={{ mb: 2 }}>
          {renderField("Job Type", job.jobType, "jobType")}
          {isEditing ? (
            <TextField fullWidth label="Salary (Max)" value={editedJob.salary?.max || ""} onChange={handleFieldChange("salary")} variant="outlined" size="small" InputProps={{ style: { height: 35 } }} sx={{ mb: 1 }} />
          ) : (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Salary:</strong> {job.salary ? formatSalary(job.salary, job.jobType) : "Not Disclosed"}
            </Typography>
          )}
          {renderField("Location", job.location, "location")}
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Role:</strong> {job.role}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Job Status:</strong> {job.jobStatus}
          </Typography>
          {isEditing ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Mentor Role:</strong>
              </Typography>
              <FormControl sx={{ width: "16rem" }}>
                <Select
                  value={editedJob.mentor_role_id || ""}
                  onChange={(e) => handleFieldChange("mentor_role_id")(e)}
                  input={<OutlinedInput label="Mentor Role" />}
                  MenuProps={MenuProps}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: '#999' }}>Select a mentor role</span>
                    }
                    return <>{mentorRoles?.find((role) => role.id === selected)?.name}</>
                  }}
                  sx={{
                    fieldset: {
                      legend: {
                        display: 'none',
                      },
                    },
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      height: "18px",
                    },
                    "& .MuiMenuItem-root": {
                      height: "30px",
                      "& input::placeholder": {
                        fontSize: "14px", // Set the placeholder font size here
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {mentorRoles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Mentor Role:</strong> {mentorRoles?.find((role) => role.id === job.mentor_role_id)?.name}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Job Description Section */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            Job Description
          </Typography>
          {isEditing ? (
            <TextField value={editedJob.jobDescription} onChange={handleFieldChange("jobDescription")} variant="outlined" size="small" multiline rows={4} fullWidth label="Job Description" InputLabelProps={{ shrink: true }} sx={{ mb: 1 }} />
          ) : (
            <Typography variant="body1" sx={{ mb: 1 }}>
              {job.jobDescription}
            </Typography>
          )}
        </Box>

        {/* Job Description URL */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            JD Document
          </Typography>
          {job.jobDescriptionURL ? (
            <a href={job.jobDescriptionURL} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "none" }}>
              {decodeURIComponent(job.jobDescriptionURL.split("/").pop() || "Job Description")}
            </a>
          ) : (
            <Typography variant="body2" sx={{ color: "gray" }}>
              No JD Uploaded
            </Typography>
          )}
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              About
            </Typography>
            {isEditing && (
              <Button variant="contained" color="primary" onClick={handleRegenerate} disabled={isRegenerating}>
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </Button>
            )}
          </Box>
          {isEditing ? (
            <Suspense fallback={<div>Loading editor...</div>}>
              <AboutEditor key={editedJob.about} content={editedJob.about || ""} onChange={handleAboutChange} isEditing={isEditing} placeholder="Enter detailed job description with formatting..." />
            </Suspense>
          ) : (
            <div className="!bg-[#120f1a] p-4 rounded-md">
            <div
                className="prose prose-sm max-w-none text-gray-600 leading-relaxed w-ful"
                dangerouslySetInnerHTML={{ __html: job.about }}
              />
            </div>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
